import { Component, ElementRef, Input, OnInit, Optional, Self, AfterViewInit } from "@angular/core";
import { ControlValueAccessor, FormControl, NgControl, Validators } from "@angular/forms";

@Component({
	selector: "app-input-date",
	styleUrls: ["./input-date.component.scss"],
	template: `
		<div class="col-md-12 col-xl-12">
			<!-- Libellé optionnel, comme app-input : un label vide ne doit pas réserver
			     de hauteur, sinon le champ se décale dans une cellule de tableau. -->
			@if (label) {
				<label class="form-label">
					{{ libelle }}
					@if (isRequired) { <span class="text-danger">*</span> }
				</label>
			}
			@if (disabled || readonly) {
				<input
					[type]="dateType"
					class="form-control flatpickr flatpickr-input dis"
					[class.is-invalid]="hasError"
					mwlFlatpickr
					[altInput]="true"
					[altFormat]="altFormat"
					[dateFormat]="dateFormat"
					[enableTime]="enableTime"
					[noCalendar]="true"
					[convertModelValue]="false"
					[placeholder]="placeholder"
					[required]="isRequired"
					[formControl]="internalControl"
					(blur)="onTouched()"
					[disabled]="disabled"
					[clickOpens]="false"
					[readonly]="true"
				/>
			} @else {
				<input
					[type]="dateType"
					class="form-control flatpickr"
					[class.is-invalid]="hasError"
					mwlFlatpickr
					[altInput]="true"
					[altFormat]="altFormat"
					[dateFormat]="dateFormat"
					[enableTime]="enableTime"
					[noCalendar]="noCalendar"
					[convertModelValue]="false"
					[placeholder]="placeholder"
					[required]="isRequired"
					[formControl]="internalControl"
					(blur)="onTouched()"
					[minDate]="minDate"
					[clickOpens]="true"
					[readonly]="false"
					[disabled]="false"
				/>
			}
			@if (hasError) {
				<div class="invalid-feedback d-block">
					@if (ngControl?.control?.errors?.["required"]) {
						Ce champ est obligatoire.
					} @else {
						Ce champ est invalide.
					}
				</div>
			} @else if (errors) {
				<div class="text-danger">
					<div *ngFor="let error of errors">{{ error }}</div>
				</div>
			}
		</div>
	`,
})
export class DateInputComponent implements ControlValueAccessor, OnInit, AfterViewInit {
	@Input() label: string = "Date";
	@Input() placeholder: string = "Date";
	@Input() required: boolean = false;
	@Input() dateType: "date" | "datetime-local" = "date";
	@Input() altFormat: string = "d/m/Y";
	@Input() dateFormat: string = "Y-m-d";
	@Input() enableTime: boolean = false;
	@Input() noCalendar: boolean = false;
	@Input() errors: string[] | null = null;
	@Input() disabled: boolean = false;
	@Input() readonly: boolean = false;
	@Input() invalid: boolean = false;
	/** Jours antérieurs désactivés dans le calendrier (ex. "2026-10-06"). Sans valeur : aucune limite. */
	@Input() minDate: string | Date | undefined = undefined;

	internalControl: FormControl = new FormControl();
	onChange: any = () => {};
	onTouched: any = () => {};

	constructor(
		@Optional() @Self() public ngControl: NgControl,
		private el: ElementRef,
	) {
		if (this.ngControl) {
			this.ngControl.valueAccessor = this;
		}
	}

	/** Majuscule à chaque mot comme `titlecase`, mais un sigle déjà en majuscules (BL, BC, ICE…) reste tel quel. */
	get libelle(): string {
		return (this.label || "").replace(/\S+/g, (mot) => (mot.length > 1 && mot === mot.toUpperCase() && mot !== mot.toLowerCase() ? mot : mot[0].toUpperCase() + mot.slice(1).toLowerCase()));
	}

	get hasError(): boolean {
		const ctrl = this.ngControl?.control;
		return !!(ctrl && ctrl.invalid && ctrl.touched);
	}

	get isRequired(): boolean {
		if (this.required) return true;
		return !!this.ngControl?.control?.hasValidator(Validators.required);
	}

	ngOnInit() {
		if (this.dateType === "datetime-local") {
			this.enableTime = true;
		}
		this.internalControl.valueChanges.subscribe((value) => {
			this.onChange(value);
		});
	}

	ngAfterViewInit(): void {
		// Run once after view init to attach style
		setTimeout(() => this.updateAltInputStyle(), 0);

		// React to validation status changes
		this.ngControl?.control?.statusChanges?.subscribe(() => {
			setTimeout(() => this.updateAltInputStyle(), 0);
		});

		// React to touched changes via a MutationObserver-free approach:
		// poll on a microtask after any form interaction
	}

	private updateAltInputStyle(): void {
		// flatpickr creates the alt input as a sibling of the original
		const inputs = this.el.nativeElement.querySelectorAll("input");
		inputs.forEach((input: HTMLInputElement) => {
			if (this.hasError) {
				
			} else {
				input.style.borderColor = "";
				input.style.boxShadow = "";
			}
		});
	}

	writeValue(value: any): void {
		this.internalControl.setValue(value, { emitEvent: false });
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = () => {
			fn();
			setTimeout(() => this.updateAltInputStyle(), 0);
		};
	}
}